import { defineComponent as _defineComponent } from 'vue'
import { toDisplayString as _toDisplayString, createTextVNode as _createTextVNode, unref as _unref, isRef as _isRef, vModelText as _vModelText, withModifiers as _withModifiers, withKeys as _withKeys, createElementVNode as _createElementVNode, withDirectives as _withDirectives, renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, vShow as _vShow, Transition as _Transition, withCtx as _withCtx, createVNode as _createVNode, createBlock as _createBlock } from "vue"

const _hoisted_1 = { class: "combobox" }
const _hoisted_2 = ["id", "placeholder", "aria-invalid", "aria-describedby", "aria-activedescendant"]
const _hoisted_3 = ["id"]
const _hoisted_4 = ["value"]
const _hoisted_5 = ["aria-label"]
const _hoisted_6 = { key: 0 }
const _hoisted_7 = ["id", "aria-selected", "onClick", "onKeyup"]

import { useCountryInput } from '@src/components/shared/form_fields/useCountryInput';
import { useFilteredCountries } from '@src/components/shared/form_fields/useFilteredCountries';
import type { Country } from '@src/view_models/Country';
import { computed, nextTick, ref } from 'vue';
import { updateAutocompleteScrollPosition } from '@src/components/shared/form_fields/updateAutocompleteScrollPosition';
import { useAriaDescribedby } from '@src/components/shared/composables/useAriaDescribedby';
import { autoscrollMaxWidth, useAutocompleteScrollIntoViewOnFocus } from '@src/components/shared/form_fields/useAutocompleteScrollIntoViewOnFocus';
import FieldContainer from '@src/components/patterns/FieldContainer.vue';

enum InteractionState {
	Typing,
	Selecting,
}

interface Props {
	modelValue: string;
	inputId: string;
	scrollTargetId: string;
	countries?: Array<Country>;
	showError: boolean;
	wasRestored: boolean;
	isMaxWidthField?: boolean;
	ariaDescribedby?: string | null;
}


export default /*@__PURE__*/_defineComponent({
  __name: 'CountryAutocompleteField',
  props: {
    modelValue: {},
    inputId: {},
    scrollTargetId: {},
    countries: {},
    showError: { type: Boolean },
    wasRestored: { type: Boolean },
    isMaxWidthField: { type: Boolean },
    ariaDescribedby: {}
  },
  emits: [ 'initialised', 'field-changed', 'update:modelValue' ],
  setup(__props: any, { emit: __emit }) {


const props = __props;
const emit = __emit;

const { country, countryName } = useCountryInput( props.modelValue, props.countries, emit as ( event: string, value: any ) => void );
const { filteredCountries, groupSeparatorIndex } = useFilteredCountries( props.countries, countryName );
const interactionState = ref<InteractionState>( InteractionState.Typing );
const activeCountry = ref<string>();
const activeCountryName = computed<string>( () => {
	return filteredCountries.value.find( x => x.countryCode === activeCountry.value )?.countryFullName;
} );
const scrollElement = ref<HTMLElement>();
const wasFocusedBefore = ref<Boolean>( false );
const autocompleteIsActive = ref<Boolean>( false );
const ariaDescribedby = useAriaDescribedby(
	props.inputId,
	computed<boolean>( () => false ),
	computed<boolean>( () => props.showError ),
	computed<boolean>( () => false ),
	computed<string | null>( () => props.ariaDescribedby )
);

const scrollIntoView = useAutocompleteScrollIntoViewOnFocus( props.scrollTargetId, autoscrollMaxWidth );

const isFirstFocusOnDefaultValue = (): boolean => {
	return !wasFocusedBefore.value && !props.wasRestored;
};

const onFocus = ( event: Event ) => {
	if ( isFirstFocusOnDefaultValue() ) {
		countryName.value = '';
	}
	wasFocusedBefore.value = true;

	autocompleteIsActive.value = true;
	scrollIntoView();
	( event.target as HTMLInputElement ).select();
};

const onKeydown = ( event: KeyboardEvent ) => {
	if ( [ 'ArrowUp', 'ArrowDown', 'Tab', 'Enter' ].includes( event.key ) ) {
		return true;
	}

	interactionState.value = InteractionState.Typing;
	activeCountry.value = undefined;
};

const onKeyArrows = async ( direction: 'up' | 'down' ) => {
	interactionState.value = InteractionState.Selecting;

	if ( activeCountry.value === undefined ) {
		activeCountry.value = filteredCountries.value[ 0 ].countryCode;
		return;
	}

	let index = filteredCountries.value.findIndex( x => x.countryCode === activeCountry.value );

	if ( direction === 'up' && index > 0 ) {
		index--;
	}

	if ( direction === 'down' && index + 1 < filteredCountries.value.length ) {
		index++;
	}

	activeCountry.value = filteredCountries.value[ index ].countryCode;

	await nextTick();
	updateAutocompleteScrollPosition( scrollElement );
};

const onKeySubmit = () => {
	if ( interactionState.value === InteractionState.Typing ) {
		return;
	}

	countryName.value = filteredCountries.value.find( x => x.countryCode === activeCountry.value )?.countryFullName;
};

let itemWasJustSelectedFromList = false;

const onInput = async (): Promise<void> => {
	if ( props.showError ) {
		await nextTick();
		emit( 'field-changed', country.value );
	}
};

const onBlur = ( selectedCountry: Country ) => {
	setTimeout( () => {
		autocompleteIsActive.value = false;

		if ( !itemWasJustSelectedFromList ) {
			emit( 'field-changed', selectedCountry ?? '' );
		}
		itemWasJustSelectedFromList = false;
	}, 200 );
};

const onSelectItem = async ( selectedCountry: Country ) => {
	itemWasJustSelectedFromList = true;
	countryName.value = selectedCountry.countryFullName;
	await nextTick();
	emit( 'field-changed', selectedCountry );
};


return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createBlock(FieldContainer, {
    "input-id": __props.inputId,
    "show-error": __props.showError,
    "is-max-width-field": __props.isMaxWidthField
  }, {
    label: _withCtx(() => [
      _createTextVNode(_toDisplayString(_ctx.$t( 'donation_form_country_label' )), 1)
    ]),
    field: _withCtx(() => [
      _createElementVNode("div", _hoisted_1, [
        _withDirectives(_createElementVNode("input", {
          type: "text",
          name: "countrySelector",
          "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => (_isRef(countryName) ? (countryName).value = $event : null)),
          id: __props.inputId,
          autocomplete: "country",
          placeholder: _ctx.$t( 'form_for_example', { example: __props.countries[0].countryFullName } ),
          "aria-controls": "countries",
          "aria-invalid": __props.showError,
          "aria-describedby": _unref(ariaDescribedby),
          "aria-autocomplete": "list",
          "aria-activedescendant": activeCountry.value ? `country-${activeCountry.value}` : null,
          onFocus: onFocus,
          onBlur: _cache[1] || (_cache[1] = () => onBlur( _unref(country) )),
          onInput: onInput,
          onKeydown: [
            onKeydown,
            _cache[2] || (_cache[2] = _withKeys(_withModifiers(($event: any) => (onKeyArrows('up')), ["prevent"]), ["up"])),
            _cache[3] || (_cache[3] = _withKeys(_withModifiers(($event: any) => (onKeyArrows('down')), ["prevent"]), ["down"])),
            _withKeys(onKeySubmit, ["tab"]),
            _withKeys(onKeySubmit, ["enter"])
          ]
        }, null, 40, _hoisted_2), [
          [_vModelText, _unref(countryName)]
        ]),
        _createElementVNode("span", {
          class: "is-sr-only",
          id: `${__props.inputId}-selected`,
          "aria-live": "assertive"
        }, _toDisplayString(activeCountryName.value), 9, _hoisted_3),
        _createElementVNode("input", {
          type: "hidden",
          name: "country",
          value: _unref(country)?.countryCode
        }, null, 8, _hoisted_4),
        _createVNode(_Transition, {
          name: "fade",
          persisted: ""
        }, {
          default: _withCtx(() => [
            _withDirectives(_createElementVNode("div", {
              id: "countries",
              ref_key: "scrollElement",
              ref: scrollElement,
              tabindex: "-1",
              role: "listbox",
              "aria-label": _ctx.$t( 'donation_form_country_list_label' )
            }, [
              (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(_unref(filteredCountries), (country, index) => {
                return (_openBlock(), _createElementBlock(_Fragment, { key: index }, [
                  (_unref(groupSeparatorIndex) === index)
                    ? (_openBlock(), _createElementBlock("hr", _hoisted_6))
                    : _createCommentVNode("", true),
                  _createElementVNode("button", {
                    tabindex: "-1",
                    role: "option",
                    id: `country-${country.countryCode}`,
                    "aria-selected": country.countryCode === activeCountry.value,
                    onClick: () => onSelectItem( country ),
                    onKeyup: _withKeys(($event: any) => (onSelectItem(country)), ["enter","space"])
                  }, _toDisplayString(country.countryFullName), 41, _hoisted_7)
                ], 64))
              }), 128))
            ], 8, _hoisted_5), [
              [_vShow, autocompleteIsActive.value]
            ])
          ]),
          _: 1
        })
      ])
    ]),
    error: _withCtx(() => [
      _createTextVNode(_toDisplayString(_ctx.$t('donation_form_country_error')), 1)
    ]),
    _: 1
  }, 8, ["input-id", "show-error", "is-max-width-field"]))
}
}

})